import { useApolloClient } from '@apollo/client'
import omit from 'lodash/omit'
import {
  findDashboardQuestionBankCategory,
  findDashboardQuestionBankCategoryVariables,
  findDashboardQuestionBankCategory_findAllQuestionBankCategory_items as QBC,
  findDashboardQuestionBankCategory_findAllQuestionBankCategory_items_questionBanks as QB,
} from '__generatedTypes__/findDashboardQuestionBankCategory'
import { CreateQuestionBankCategoryInput, OrderDirection, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import {
  findDashboardPracticeTestCategory,
  findDashboardPracticeTestCategoryVariables,
  findDashboardPracticeTestCategory_findAllPracticeTestCategory_items as PracticeTestCategory,
} from '__generatedTypes__/findDashboardPracticeTestCategory'
import {
  bulkUpdateQuestionBankCategory,
  bulkUpdateQuestionBankCategoryVariables,
} from '__generatedTypes__/bulkUpdateQuestionBankCategory'
import {
  bulkUpdatePracticeTestCategory,
  bulkUpdatePracticeTestCategoryVariables,
} from '__generatedTypes__/bulkUpdatePracticeTestCategory'
import {
  updateQuestionBankCategory,
  updateQuestionBankCategoryVariables,
} from '__generatedTypes__/updateQuestionBankCategory'
import {
  updatePracticeTestCategory,
  updatePracticeTestCategoryVariables,
} from '__generatedTypes__/updatePracticeTestCategory'
import { bulkUpdateQuestionBank, bulkUpdateQuestionBankVariables } from '__generatedTypes__/bulkUpdateQuestionBank'
import { bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables } from '__generatedTypes__/bulkUpdatePracticeTest'
import { updateQuestionBank, updateQuestionBankVariables } from '__generatedTypes__/updateQuestionBank'
import { updatePracticeTest, updatePracticeTestVariables } from '__generatedTypes__/updatePracticeTest'
import {
  createQuestionBankCategory,
  createQuestionBankCategoryVariables,
} from '__generatedTypes__/createQuestionBankCategory'
import {
  createPracticeTestCategory,
  createPracticeTestCategoryVariables,
} from '__generatedTypes__/createPracticeTestCategory'
import {
  removeQuestionBankCategory,
  removeQuestionBankCategoryVariables,
} from '__generatedTypes__/removeQuestionBankCategory'
import {
  removePracticeTestCategory,
  removePracticeTestCategoryVariables,
} from '__generatedTypes__/removePracticeTestCategory'
import { BULK_UPDATE_PRACTICE_TEST, UPDATE_PRACTICE_TEST } from 'queries/practiceTest'
import { BULK_UPDATE_QUESTION_BANK, UPDATE_QUESTION_BANK } from 'queries/questionBanks'
import {
  BULK_UPDATE_PRACTICE_TEST_CATEGORY,
  CREATE_PRACTICE_TEST_CATEGORY,
  DELETE_PRACTICE_TEST_CATEGORY,
  GET_DASHBOARD_PRACTICE_TEST_CATEGORY,
  UPDATE_PRACTICE_TEST_CATEGORY,
} from 'queries/practiceTestCategory'
import {
  BULK_UPDATE_QUESTION_BANK_CATEGORY,
  CREATE_QUESTION_BANK_CATEGORY,
  DELETE_QUESTION_BANK_CATEGORY,
  GET_DASHBOARD_QUESTION_BANK_CATEGORY,
  UPDATE_QUESTION_BANK_CATEGORY,
} from 'queries/questionBankCategory'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { SubjectExtended } from 'hooks/usePlatformsAndSubjects'
import { isReadingSubject } from 'pagesNew/QuestionBanks/utils'

export type QuestionBank = Omit<QB, '__typename'> & {
  type?: PracticeTestTypeEnum
}
export type QuestionBankCategory = Omit<QBC, '__typename' | 'questionBanks'> & {
  questionBanks: QuestionBank[]
}

export const useCategoriesApi = () => {
  const { query, mutate } = useApolloClient()

  const getCategories = async (subject: SubjectExtended): Promise<QuestionBankCategory[]> => {
    if (isReadingSubject(subject.slug)) {
      const { data } = await query<findDashboardPracticeTestCategory, findDashboardPracticeTestCategoryVariables>({
        query: GET_DASHBOARD_PRACTICE_TEST_CATEGORY,
        variables: {
          params: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              subject: {
                id: subject.id,
              },
            },
            order: {
              order: OrderDirection.ASC,
            },
          },
        },
        fetchPolicy: 'network-only',
      })

      return data.findAllPracticeTestCategory.items.map(
        (practiceTestCategory: PracticeTestCategory) =>
          ({
            ...practiceTestCategory,
            tabIndex: 1,
            questionBanks: practiceTestCategory.practiceTests,
          } as QuestionBankCategory)
      )
    }

    const { data } = await query<findDashboardQuestionBankCategory, findDashboardQuestionBankCategoryVariables>({
      query: GET_DASHBOARD_QUESTION_BANK_CATEGORY,
      variables: {
        params: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            subject: {
              id: subject.id,
            },
            tabIndex: subject.tabIndex,
          },
          order: {
            order: OrderDirection.ASC,
          },
        },
      },
      fetchPolicy: 'network-only',
    })

    return data.findAllQuestionBankCategory.items
  }

  const createCategory = async (subject: SubjectExtended, category: CreateQuestionBankCategoryInput) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<createPracticeTestCategory, createPracticeTestCategoryVariables>({
        mutation: CREATE_PRACTICE_TEST_CATEGORY,
        variables: {
          params: omit(category, 'tabIndex'),
        },
      })
      return
    }

    await mutate<createQuestionBankCategory, createQuestionBankCategoryVariables>({
      mutation: CREATE_QUESTION_BANK_CATEGORY,
      variables: {
        params: category,
      },
    })
  }

  const updateCategory = async (
    subject: SubjectExtended,
    categoryId: string,
    category: CreateQuestionBankCategoryInput
  ) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<updatePracticeTestCategory, updatePracticeTestCategoryVariables>({
        mutation: UPDATE_PRACTICE_TEST_CATEGORY,
        variables: {
          params: {
            id: categoryId,
            ...omit(category, 'tabIndex'),
          },
        },
      })
      return
    }

    await mutate<updateQuestionBankCategory, updateQuestionBankCategoryVariables>({
      mutation: UPDATE_QUESTION_BANK_CATEGORY,
      variables: {
        params: {
          id: categoryId,
          ...category,
        },
      },
    })
  }

  const removeCategory = async (subject: SubjectExtended, categoryId: string) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<removePracticeTestCategory, removePracticeTestCategoryVariables>({
        mutation: DELETE_PRACTICE_TEST_CATEGORY,
        variables: {
          params: categoryId,
        },
      })
      return
    }

    await mutate<removeQuestionBankCategory, removeQuestionBankCategoryVariables>({
      mutation: DELETE_QUESTION_BANK_CATEGORY,
      variables: {
        params: categoryId,
      },
    })
  }

  const updateCategoriesOrder = async (subject: SubjectExtended, categories: QuestionBankCategory[]) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<bulkUpdatePracticeTestCategory, bulkUpdatePracticeTestCategoryVariables>({
        mutation: BULK_UPDATE_PRACTICE_TEST_CATEGORY,
        variables: {
          params: categories.map(({ id }, order) => ({ id, order })),
        },
      })
      return
    }

    await mutate<bulkUpdateQuestionBankCategory, bulkUpdateQuestionBankCategoryVariables>({
      mutation: BULK_UPDATE_QUESTION_BANK_CATEGORY,
      variables: {
        params: categories.map(({ id }, order) => ({ id, order })),
      },
    })
  }

  const updateCategoryIsLive = async (subject: SubjectExtended, categoryId: string, isLive: boolean) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<updatePracticeTestCategory, updatePracticeTestCategoryVariables>({
        mutation: UPDATE_PRACTICE_TEST_CATEGORY,
        variables: {
          params: {
            id: categoryId,
            isLive,
          },
        },
      })
      return
    }

    await mutate<updateQuestionBankCategory, updateQuestionBankCategoryVariables>({
      mutation: UPDATE_QUESTION_BANK_CATEGORY,
      variables: {
        params: {
          id: categoryId,
          isLive,
        },
      },
    })
  }

  const updateQBsOrder = async (subject: SubjectExtended, qbs: QuestionBank[]) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables>({
        mutation: BULK_UPDATE_PRACTICE_TEST,
        variables: {
          params: qbs.map(({ id }, order) => ({ id, order })),
        },
      })
      return
    }

    await mutate<bulkUpdateQuestionBank, bulkUpdateQuestionBankVariables>({
      mutation: BULK_UPDATE_QUESTION_BANK,
      variables: {
        params: qbs.map(({ id }, order) => ({ id, order })),
      },
    })
  }

  const updateQBIsLive = async (subject: SubjectExtended, qbId: string, isLive: boolean) => {
    if (isReadingSubject(subject.slug)) {
      await mutate<updatePracticeTest, updatePracticeTestVariables>({
        mutation: UPDATE_PRACTICE_TEST,
        variables: {
          params: {
            id: qbId,
            isLive,
          },
        },
      })
      return
    }

    await mutate<updateQuestionBank, updateQuestionBankVariables>({
      mutation: UPDATE_QUESTION_BANK,
      variables: {
        params: {
          id: qbId,
          isLive,
        },
      },
    })
  }

  return {
    getCategories,
    createCategory,
    updateCategory,
    removeCategory,
    updateCategoriesOrder,
    updateCategoryIsLive,
    updateQBsOrder,
    updateQBIsLive,
  }
}
